/**
 * Landing bo'limlari — har biri alohida sahifa (ЧТЗ 4.2.11 bloklari).
 * Sarlavha, menyu va futer `PublicLayout` (layout route) da bir marta chiziladi,
 * shu sababli sahifalar almashganda ular o'zgarmaydi.
 */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { makeContacts, subsystems } from "@/shared/data/content";
import { usePortalConfig, usePortalHome } from "@/shared/data/portal";
import { useI18n } from "@/shared/i18n";
import { useAutoScroll, useReveal } from "@/shared/lib/motion";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { Icon } from "@/shared/ui/Icon";

import { AboutSection, useStatistics } from "./landing/AboutSection";
import { AppealForm } from "./landing/AppealForm";
import { MobileAppSection } from "./landing/MobileAppSection";
import { PublicGeoportal } from "./landing/PublicGeoportal";
import { ServiceGateway } from "./landing/ServiceGateway";
import { BareHeadContext, SplitHead } from "./landing/SplitHead";
import { SectionScenes } from "./landing/scenes3d/SectionScenes";
import { TrackingSection } from "./landing/TrackingSection";
import { usePortalAbout } from "./landing/publicData";
import { stagger, trackPointer } from "./landing/helpers";

/** Bo'lim sahifasi: kontent + shu sahifadagi 3D sahnalar (sahifa almashganda qayta quriladi). */
export function SectionPage({ children, revealKey = "" }: { children: ReactNode; revealKey?: string }) {
  useReveal([revealKey]);
  return (
    <div className="section-page">
      <BareHeadContext.Provider value>{children}</BareHeadContext.Provider>
      <SectionScenes />
    </div>
  );
}

export function AboutPage() {
  const statistics = useStatistics();
  const about = usePortalAbout();
  return (
    <SectionPage revealKey={`${statistics.items.length}-${statistics.charts ? 1 : 0}-${about.data ? 1 : 0}`}>
      <AboutSection />
    </SectionPage>
  );
}

export function SubsystemsPage() {
  const { t } = useI18n();
  const laneRef = useRef<HTMLDivElement | null>(null);
  const [lanePaused, setLanePaused] = useState(false);
  /* Ro'yxat ikki nusxada beriladi — avtonom siljish uzilishsiz aylanadi. */
  const laneItems = [...subsystems, ...subsystems];
  useAutoScroll(laneRef, { paused: lanePaused });

  return (
    <SectionPage>
      <section className="section" id="subsystems">
        <div className="container">
          <SplitHead
            scene="subsystems"
            eyebrow={t("nav.subsystems")}
            title="13 ta o'zaro bog'langan kichik tizim"
            lead="Yagona ma'lumot manbai: bir joyda kiritilgan ma'lumot qayta so'ralmaydi."
          />

          <div className="tiles-lane">
            <div
              className="tiles"
              ref={laneRef}
              onPointerMove={trackPointer}
              role="region"
              aria-label="Kichik tizimlar ro'yxati"
              tabIndex={0}
            >
              {laneItems.map((item, index) => {
                const isClone = index >= subsystems.length;
                return (
                  <article
                    className="tile"
                    key={`${item.num}-${index}`}
                    data-spotlight
                    {...(isClone ? { "aria-hidden": true } : { "data-reveal": "" })}
                    style={stagger(index % 4)}
                  >
                    <span className="tile-num num" aria-hidden="true">
                      {item.num}
                    </span>
                    <span className="tile-icon">
                      <Icon name={item.icon} size={22} />
                    </span>
                    <h3>{item.title}</h3>
                  </article>
                );
              })}
            </div>

            {/* WCAG 2.2.2 — o'zi harakatlanadigan kontentni to'xtatish imkoni. */}
            <button
              className="tiles-pause"
              type="button"
              aria-pressed={lanePaused}
              onClick={() => setLanePaused((value) => !value)}
            >
              <Icon name={lanePaused ? "play" : "pause"} size={16} />
              {lanePaused ? "Davom ettirish" : "To'xtatish"}
            </button>
          </div>
        </div>
      </section>
    </SectionPage>
  );
}

export function ServicesPage() {
  const { t } = useI18n();
  const home = usePortalHome();
  return (
    <SectionPage revealKey={home ? "1" : "0"}>
      <section className="section section-alt" id="services">
        <div className="container">
          <SplitHead
            scene="services"
            side="right"
            eyebrow={t("nav.services")}
            title="Fuqarolar va biznes uchun xizmatlar"
            lead="Murojaat va ochiq ma'lumotlar — ro'yxatdan o'tmasdan; shartnoma va to'lovlar — OneID orqali."
          />

          <ServiceGateway />
        </div>
      </section>
    </SectionPage>
  );
}

/**
 * Murojaat sahifasi: yuborish formasi va uning ostida «Murojaatni kuzatish».
 * `/kuzatish` (va SMS'dagi eski `/track`) shu sahifani ochib, kuzatish qismiga
 * aylantiradi; `?id=` bo'lsa ID maydoni to'ldiriladi.
 */
export function AppealPage({ focusTracking = false }: { focusTracking?: boolean }) {
  const home = usePortalHome();
  const config = usePortalConfig();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ticket = params.get("id") ?? "";

  useEffect(() => {
    if (!focusTracking) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById("tracking")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [focusTracking, ticket]);

  return (
    <SectionPage revealKey={home ? "1" : "0"}>
      <AppealForm
        home={home}
        onTrack={(appealId) => navigate(`${SECTION_PATHS.tracking}?id=${encodeURIComponent(appealId)}`)}
      />
      <TrackingSection initialId={ticket} hotline={config.hotline} />
    </SectionPage>
  );
}

export function TrackingPage() {
  return <AppealPage focusTracking />;
}

export function MapPage() {
  return (
    <SectionPage>
      <PublicGeoportal />
    </SectionPage>
  );
}

export function MobileAppPage() {
  const home = usePortalHome();
  return (
    <SectionPage revealKey={home ? "1" : "0"}>
      <MobileAppSection home={home} />
    </SectionPage>
  );
}

export function ContactPage() {
  const { t } = useI18n();
  const config = usePortalConfig();
  const contactItems = makeContacts(config);
  return (
    <SectionPage>
      <section className="section" id="contact">
        <div className="container">
          <SplitHead
            scene="contact"
            eyebrow={t("nav.contacts")}
            title="Biz bilan bog'lanish"
            lead="Savol, taklif yoki shikoyat bo'yicha quyidagi kanallardan foydalaning."
          >
            <a className="btn btn-primary" href={`tel:${config.hotline}`}>
              <Icon name="phone" size={16} />
              {config.hotline}
            </a>
          </SplitHead>

          <div className="contact-grid">
            {contactItems.map((item, index) => (
              <article
                className="contact-card"
                key={item.label}
                data-reveal
                style={stagger(index, 80)}
              >
                <span className="contact-icon">
                  <Icon name={item.icon} size={22} />
                </span>
                <span className="contact-label">{item.label}</span>
                {item.href ? (
                  <a className="contact-value" href={item.href}>
                    {item.value}
                  </a>
                ) : (
                  <span className="contact-value">{item.value}</span>
                )}
                <span className="contact-note">{item.note}</span>
              </article>
            ))}
          </div>
        </div>
      </section>
    </SectionPage>
  );
}
